var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable no-underscore-dangle */
import { isNumber } from '@design-stack-ct/utility-core';
import { action, computed, makeObservable, observable } from 'mobx';
import { vectorizer } from '../vectorizer';
import { getSingleColor, getSingleColorThresholding, getInvertedColor, detectColors, getSepiaColor } from '../algolib';
import { queryAssets, getMetadata, createFolder, getAsset, presignUrl, } from '../client';
import { CombinedAbortController, isAbortError, parseAssetUrl, serializeWithSort } from '../utilities';
import { Asset } from './entities/Asset';
import { isFetchSingleAssetByMetadataParams, isFetchSingleAssetByUrlParams, isFileUploadParams } from './typeGuards';
import { AssetStoreFetchStatusType, } from './types';
import { AssetErrorMessage, AssetStatusType, } from './entities/types';
import { assetStoreEvent, AssetStoreEvent, AssetStoreEventTarget } from './assetStoreEvent';
import { getAssetV3, getMetadataV3 } from '../client/sherbertClientV3';
import { isRoute, Route } from '../artworkProcessing/utility';
export class AssetStore {
    /** @param params */
    constructor({ authProvider, tenant }) {
        Object.defineProperty(this, "authProvider", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "tenant", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "singleAssetCache", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "previousFetchParams", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_assets", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "_fetchStatus", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: { type: AssetStoreFetchStatusType.Idle }
        });
        Object.defineProperty(this, "assetStoreEventTarget", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "assetsRecords", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "fetchAssetsAbortController", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "assetCache", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        // Utility function for polling image info
        Object.defineProperty(this, "pollForImageInfo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (queryAsset, maxRetries = 30, delay = 1000) => new Promise((resolve) => {
                let retries = 0;
                const interval = setInterval(() => {
                    var _a;
                    if ((_a = queryAsset.data) === null || _a === void 0 ? void 0 : _a.info.image) {
                        clearInterval(interval);
                        resolve();
                    }
                    else if (retries >= maxRetries) {
                        clearInterval(interval);
                        resolve(); // or reject() if you prefer to handle missing image info as an error
                    }
                    retries += 1;
                }, delay);
            })
        });
        makeObservable(this);
        this.assetStoreEventTarget = new AssetStoreEventTarget();
        this.tenant = tenant;
        this.authProvider = authProvider;
        this.singleAssetCache = new Map();
    }
    get assets() {
        return this._assets.filter((asset) => asset.status.type !== AssetStatusType.Deleted);
    }
    /** Current status of fetching assets. */
    get fetchStatus() {
        return this._fetchStatus;
    }
    /**
     * Fetches assets based on the provided parameters.
     * Defaults to fetching 20 assets. Pass `noFetchLimit` as the limit parameter to fetch all assets.
     *
     * @param params
     */
    fetchAssets(_a = {}) {
        var _b;
        var { signal, limit = 20, query = {} } = _a, queryAssetsParams = __rest(_a, ["signal", "limit", "query"]);
        return __awaiter(this, void 0, void 0, function* () {
            (_b = this.fetchAssetsAbortController) === null || _b === void 0 ? void 0 : _b.abort();
            this.setAssets([]);
            const assets = yield this._fetchAssets(Object.assign({ signal, limit, query }, queryAssetsParams));
            this.setAssets(assets);
        });
    }
    get canFetchMore() {
        var _a;
        return !!((_a = this.previousFetchParams) === null || _a === void 0 ? void 0 : _a.offset);
    }
    /**
     * Paginate assets based on provided parameters.
     * Continues where `fetchAssets` left off, using the same sorting direction.
     *
     * @param params
     */
    fetchMore({ signal, limit } = {}) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.canFetchMore) {
                const newLimit = limit !== null && limit !== void 0 ? limit : (_a = this.previousFetchParams) === null || _a === void 0 ? void 0 : _a.limit;
                const assets = yield this._fetchAssets(Object.assign(Object.assign({}, this.previousFetchParams), { limit: newLimit, signal }));
                this.appendAssets(...assets);
            }
        });
    }
    fetchSingleAsset(params) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const { signal } = params;
            const headers = new Headers(yield this.authProvider.getAuthHeaders());
            let singleAsset;
            if (params.route !== null && params.route !== undefined && !isRoute(params.route)) {
                throw new Error(`${params.route} is not a valid route`);
            }
            if (isFetchSingleAssetByMetadataParams(params)) {
                const { query } = params;
                // We're caching single assets queried by metadata
                // using a stringified query object that `fetchSingleAsset` was called with.
                // This is not ideal, but it saves us from having to maintain a copy of Sherbert's querying algorithm.
                const queryCacheKey = serializeWithSort(query);
                const localAsset = this.singleAssetCache.get(queryCacheKey);
                if (localAsset) {
                    return localAsset;
                }
                const { data: queryResult } = yield queryAssets({ queryParams: { limit: 1, showHidden: true }, body: query }, { signal, headers });
                if (queryResult.count !== 1) {
                    throw new Error(AssetErrorMessage.AssetNotFound);
                }
                const [assetData] = queryResult._embedded.item;
                singleAsset = Asset.fromData({
                    assetData,
                    authProvider: this.authProvider,
                    assetStoreEventTarget: this.assetStoreEventTarget,
                });
                this.singleAssetCache.set(assetData.id, singleAsset);
                this.singleAssetCache.set(queryCacheKey, singleAsset);
            }
            else {
                const { id, signature } = isFetchSingleAssetByUrlParams(params) ? parseAssetUrl(params.url) : params;
                // try to find the asset in the singleAssetCache first, then in the paginated assets
                const localAsset = (_a = this.singleAssetCache.get(id)) !== null && _a !== void 0 ? _a : this._assets.find((asset) => { var _a; return ((_a = asset.data) === null || _a === void 0 ? void 0 : _a.id) === id; });
                if (localAsset) {
                    return localAsset;
                }
                try {
                    let assetData;
                    switch (params.route || Route.V3) {
                        case Route.V2:
                            assetData = (yield getMetadata({ path: { id }, queryParams: signature ? { signature } : {} }, { signal, headers })).data;
                            break;
                        case Route.V3:
                            assetData = (yield getMetadataV3({ path: { id }, queryParams: signature ? { signature } : {} }, { signal, headers })).data;
                            break;
                        default:
                            throw new Error(`${params.route} is not a valid route`);
                    }
                    singleAsset = Asset.fromData({
                        assetData,
                        authProvider: this.authProvider,
                        signature,
                        assetStoreEventTarget: this.assetStoreEventTarget,
                    });
                    this.singleAssetCache.set(id, singleAsset);
                }
                catch (error) {
                    throw new Error(error);
                }
            }
            return singleAsset;
        });
    }
    upload(_a, callback) {
        var _b;
        var { route, signal, signedUrl } = _a, params = __rest(_a, ["route", "signal", "signedUrl"]);
        return __awaiter(this, void 0, void 0, function* () {
            let asset;
            if (isFileUploadParams(params)) {
                asset = Asset.fromFile(Object.assign(Object.assign({ tenant: this.tenant }, params), { authProvider: this.authProvider, assetStoreEventTarget: this.assetStoreEventTarget }));
            }
            else if ('data' in params) {
                asset = Asset.fromDocumentItem(Object.assign(Object.assign({ tenant: this.tenant }, params), { authProvider: this.authProvider, assetStoreEventTarget: this.assetStoreEventTarget }));
            }
            else {
                asset = Asset.fromUri(Object.assign(Object.assign({ tenant: this.tenant }, params), { authProvider: this.authProvider, assetStoreEventTarget: this.assetStoreEventTarget }));
            }
            this.prependAssets(asset);
            this.assetStoreEventTarget.dispatchEvent(assetStoreEvent(AssetStoreEvent.UploadStart, { detail: asset }));
            if (signedUrl) {
                yield asset.prepare({ signal, signedUrl }, callback);
            }
            else {
                yield asset.prepare({ signal, route }, callback);
            }
            if (((_b = asset.data) === null || _b === void 0 ? void 0 : _b.id) && !this.assetCache[asset.data.id]) {
                this.assetCache[asset.data.id] = asset;
            }
            if (asset.status.type === AssetStatusType.Error || asset.status.type === AssetStatusType.SignedUrlError) {
                this.assetStoreEventTarget.dispatchEvent(assetStoreEvent(AssetStoreEvent.UploadError, { detail: asset }));
            }
            else if (asset.status.type === AssetStatusType.Prepared) {
                this.assetStoreEventTarget.dispatchEvent(assetStoreEvent(AssetStoreEvent.UploadSuccess, { detail: asset }));
            }
            return asset;
        });
    }
    uploadByPlaceHolder(tempAssetData) {
        return __awaiter(this, void 0, void 0, function* () {
            const asset = tempAssetData;
            this.prependAssets(asset);
            this.assetStoreEventTarget.dispatchEvent(assetStoreEvent(AssetStoreEvent.UploadStart, { detail: asset }));
            yield asset.setPlaceHolderAsset(asset);
            if (asset.status.type === AssetStatusType.Error) {
                this.assetStoreEventTarget.dispatchEvent(assetStoreEvent(AssetStoreEvent.UploadError, { detail: asset }));
            }
            else if (asset.status.type === AssetStatusType.Prepared) {
                this.assetStoreEventTarget.dispatchEvent(assetStoreEvent(AssetStoreEvent.UploadSuccess, { detail: asset }));
            }
            return asset;
        });
    }
    /**
     * Creates a vectorizer image based url provided.
     * @param {VectorizerImageParams} ParametersObject Object holding url and usePortrace by default usePortrace is true
     * @returns {Promise<Blob>} Returns an vectorizer Blob object
     */
    fetchVectorizerImage({ url }) {
        return __awaiter(this, void 0, void 0, function* () {
            const svg = yield this.getVectorizerImage({ url });
            const blob = new Blob([svg], { type: 'image/svg+xml' });
            return blob;
        });
    }
    /**
     * Creates a SepiaColorImage image based provided.
     * @param {SepiaColorParams} ParametersObject Object holding file
     * @returns {Promise<any>} Returns an sepiaColor colored Blob or Raw as per iOFormat object
     */
    fetchSepiaColorImage({ file, resizeImage, iOFormat }) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.getSepiaColorImage({ file, resizeImage, iOFormat });
        });
    }
    /**
     * Creates a  image based provided.
     * @param {SingleColorImageParams} ParametersObject Object holding file
     * @returns {Promise<Blob>} Returns an single colored Blob object
     */
    fetchSingleColor({ file, hexColorCode }) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.getSingleColorImage({ file, hexColorCode });
        });
    }
    /**
     * Creates a inverted color image based provided.
     * @param {InvertedColorImageParams} ParametersObject Object holding file
     * @returns {Promise<Blob>} Returns an inverted colored Blob object
     */
    fetchInvertedColor({ file }) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.getInvertedColor({ file });
        });
    }
    /**
     * Gives information about colors and their percentage in the image .
     * @param {DetectColorParams} ParametersObject Object holding file
     * @returns {colorDetectInfoResponse} Returns a json object containting information about the colors
     */
    detectColors({ file }) {
        return __awaiter(this, void 0, void 0, function* () {
            const colordetect = this.getDetectColors({ file });
            return colordetect;
        });
    }
    /**
     * Creates a threasolding image based on thresholdingPercentage provided.
     * @param {SingleColorImageThresholdingParams} ParametersObject Object holding file and thresholdingPercentage. Default thresholdingPercentage is 75.
     * @returns {Promise<Blob>} Returns an thresholded single color Blob object
     */
    fetchSingleColorWithThresholding({ file, thresholdingPercentage, hexColorCode, inverted = false, }) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.getSingleColorImageWithThresholding({ file, thresholdingPercentage, hexColorCode, inverted });
        });
    }
    _fetchAssets(_a = {}) {
        var _b, _c, _d;
        var { signal, limit = 20, query = {} } = _a, queryAssetsParams = __rest(_a, ["signal", "limit", "query"]);
        return __awaiter(this, void 0, void 0, function* () {
            // We need a closure over "our" version of fetchAssetsAbortController,
            // so that we can check later if its signal was aborted
            const fetchAssetsAbortController = new AbortController();
            this.fetchAssetsAbortController = fetchAssetsAbortController;
            const combinedAbortController = new CombinedAbortController([signal, fetchAssetsAbortController.signal]);
            this.setFetchStatus({ type: AssetStoreFetchStatusType.Fetching });
            try {
                const headers = new Headers(yield this.authProvider.getAuthHeaders());
                const queryParams = Object.assign(Object.assign({}, (isNumber(limit) && { limit })), queryAssetsParams);
                const { data } = yield queryAssets({ queryParams: Object.assign({ tenant: this.tenant }, queryParams), body: query }, { signal: combinedAbortController.signal, headers });
                this.assetsRecords = data;
                this.setPreviousFetchParams(Object.assign(Object.assign({}, queryParams), { offset: data.offset, query }));
                this.setFetchStatus({ type: AssetStoreFetchStatusType.Idle });
                return ((_d = (_c = (_b = data._embedded) === null || _b === void 0 ? void 0 : _b.item) === null || _c === void 0 ? void 0 : _c.map((assetData) => Asset.fromData({
                    assetData,
                    authProvider: this.authProvider,
                    assetStoreEventTarget: this.assetStoreEventTarget,
                }))) !== null && _d !== void 0 ? _d : []);
            }
            catch (error) {
                if (isAbortError(error)) {
                    // If aborted externally, set status to idle.
                    // if aborted internally, do nothing - don't want to interfere with the new call
                    if (!(fetchAssetsAbortController === null || fetchAssetsAbortController === void 0 ? void 0 : fetchAssetsAbortController.signal.aborted) && (signal === null || signal === void 0 ? void 0 : signal.aborted)) {
                        this.setFetchStatus({ type: AssetStoreFetchStatusType.Idle });
                    }
                }
                else {
                    this.setFetchStatus({ type: AssetStoreFetchStatusType.Error, rawError: error });
                }
            }
            return [];
        });
    }
    prependAssets(...assets) {
        this._assets.unshift(...assets);
    }
    appendAssets(...assets) {
        this._assets.push(...assets);
    }
    setAssets(assets) {
        this._assets = assets;
    }
    setFetchStatus(fetchStatus) {
        this._fetchStatus = fetchStatus;
    }
    setPreviousFetchParams(previousFetchParams) {
        this.previousFetchParams = previousFetchParams;
    }
    // eslint-disable-next-line class-methods-use-this
    getSepiaColorImage({ file, resizeImage, iOFormat }) {
        return getSepiaColor(file, resizeImage, iOFormat);
    }
    // eslint-disable-next-line class-methods-use-this
    getSingleColorImage({ file, hexColorCode }) {
        return getSingleColor(file, hexColorCode);
    }
    // eslint-disable-next-line class-methods-use-this
    getVectorizerImage({ url }) {
        return vectorizer(url);
    }
    // eslint-disable-next-line class-methods-use-this
    getInvertedColor({ file }) {
        return getInvertedColor(file);
    }
    // eslint-disable-next-line class-methods-use-this
    getDetectColors({ file }) {
        return detectColors(file);
    }
    // eslint-disable-next-line class-methods-use-this
    getSingleColorImageWithThresholding({ file, thresholdingPercentage, hexColorCode, inverted, }) {
        return getSingleColorThresholding(file, thresholdingPercentage, hexColorCode, inverted);
    }
    /**
     * creates a subfolder based on parentId.
     * @param {CreateFolderParams} ParametersObject Object holding creteFolder parameters for this method call.
     * @returns {Promise<AssetData>} Returns an AssetData Promise object.Throws an error on createFolder failure .
     */
    createFolder(folderParams) {
        return __awaiter(this, void 0, void 0, function* () {
            const { signal } = folderParams;
            try {
                const headers = new Headers(yield this.authProvider.getAuthHeaders());
                const response = yield createFolder({ body: Object.assign({}, folderParams) }, { signal, headers });
                return response.data;
            }
            catch (error) {
                throw error.message;
            }
        });
    }
    /**
     *  Generates pre-signed URL for creating assets
     * @param {CreatePreSignedUrlParams} params Object holding createPreSignedUrl parameters for this method call.
     * @returns {Promise<CreatePresignedUrlResponse>} Returns an CreatePresignedUrlResponse Promise. Throws an error on createPreSignedUrl failure .
     */
    createPreSignedUrl(params = {}, bodyParams = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const headers = new Headers(yield this.authProvider.getAuthHeaders());
                const response = (yield presignUrl({
                    queryParams: Object.assign(Object.assign({}, params), { tenant: params.tenant !== null && params.tenant !== undefined ? params.tenant : this.tenant }),
                    body: Object.assign({}, bodyParams),
                }, { headers })).data;
                if (response) {
                    return response;
                }
            }
            catch (error) {
                throw error.message;
            }
            throw new Error('Unable to generate pre-signed url.');
        });
    }
    /**
     * Polling latest assets
     * @param {PollingOptions} params takes `pollingIntervalMs` by default it is 10 seconds and `expiryTimeMs` in milliseconds by default it is 10 minutes.
     * @returns {Promise<void>} Returns a void promise. Throws an error on pollForAsset failure .
     */
    pollForNewlyUploadedAsset({ expiryTimeMs = 600000, pollingIntervalMs = 10000, signal, callback, } = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            this.assets.forEach((asset) => {
                var _a, _b;
                if ((_a = asset.data) === null || _a === void 0 ? void 0 : _a.id) {
                    this.assetCache[(_b = asset.data) === null || _b === void 0 ? void 0 : _b.id] = asset;
                }
            });
            let newlyAddedAssets = [];
            let pollingId;
            const endTime = Date.now() + expiryTimeMs;
            const abort = () => {
                clearTimeout(pollingId);
            };
            if (signal) {
                signal.addEventListener('abort', abort, { once: true });
            }
            const pollingFunction = () => __awaiter(this, void 0, void 0, function* () {
                try {
                    const assets = yield this._fetchAssets();
                    newlyAddedAssets = yield this.getNewlyAddedAssets(assets);
                    if ((newlyAddedAssets === null || newlyAddedAssets === void 0 ? void 0 : newlyAddedAssets.length) !== 0) {
                        this.assetStoreEventTarget.dispatchEvent(assetStoreEvent(AssetStoreEvent.NewlyUploadedAssetPollingSuccess, {
                            detail: newlyAddedAssets,
                        }));
                        if (callback instanceof Function) {
                            callback(newlyAddedAssets);
                        }
                        newlyAddedAssets = [];
                    }
                }
                catch (error) {
                    this.assetStoreEventTarget.dispatchEvent(assetStoreEvent(AssetStoreEvent.NewlyUploadedAssetPollingFailure, { detail: error.message }));
                    if (callback instanceof Function) {
                        callback(error);
                    }
                    throw error;
                }
                if (Date.now() < endTime) {
                    pollingId = setTimeout(pollingFunction, pollingIntervalMs);
                }
            });
            pollingId = setTimeout(pollingFunction, pollingIntervalMs);
            setTimeout(() => {
                clearTimeout(pollingId);
            }, expiryTimeMs);
        });
    }
    getNewlyAddedAssets(assets) {
        return __awaiter(this, void 0, void 0, function* () {
            const newlyAddedAssets = [];
            const presignPromises = assets.map((queryAsset) => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c, _d;
                if (((_a = queryAsset.data) === null || _a === void 0 ? void 0 : _a.type) === 'image')
                    yield this.pollForImageInfo(queryAsset);
                if (((_b = queryAsset.data) === null || _b === void 0 ? void 0 : _b.id) && !this.assetCache[(_c = queryAsset.data) === null || _c === void 0 ? void 0 : _c.id]) {
                    this.assetCache[(_d = queryAsset.data) === null || _d === void 0 ? void 0 : _d.id] = queryAsset;
                    yield queryAsset.presign();
                    this.prependAssets(queryAsset);
                    newlyAddedAssets.push(queryAsset);
                }
            }));
            yield Promise.all(presignPromises);
            return newlyAddedAssets;
        });
    }
    /**
     * returns asset based on assetsId.
     * @param {getAssetParams} params Object holding getAsset parameters for this method call.
     * @returns {Promise<void>} Returns void Promise.Throws an error on getAsset failure .
     */
    getAsset(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const { id, route } = params;
            if (route !== null && route !== undefined && !isRoute(route)) {
                throw new Error(`${route} is not a valid route`);
            }
            try {
                switch (route || Route.V3) {
                    case Route.V2:
                        yield getAsset({ path: { id } }, { headers: new Headers(yield this.authProvider.getAuthHeaders()) });
                        break;
                    case Route.V3:
                        yield getAssetV3({ path: { id } }, { headers: new Headers(yield this.authProvider.getAuthHeaders()) });
                        break;
                    default:
                        throw new Error(`${route} is not a valid route`);
                }
            }
            catch (error) {
                throw error.message;
            }
        });
    }
}
__decorate([
    observable
], AssetStore.prototype, "previousFetchParams", void 0);
__decorate([
    observable
], AssetStore.prototype, "_assets", void 0);
__decorate([
    observable
], AssetStore.prototype, "_fetchStatus", void 0);
__decorate([
    computed
], AssetStore.prototype, "assets", null);
__decorate([
    computed
], AssetStore.prototype, "fetchStatus", null);
__decorate([
    action.bound
], AssetStore.prototype, "fetchAssets", null);
__decorate([
    computed
], AssetStore.prototype, "canFetchMore", null);
__decorate([
    action.bound
], AssetStore.prototype, "fetchMore", null);
__decorate([
    action.bound
], AssetStore.prototype, "fetchSingleAsset", null);
__decorate([
    action.bound
], AssetStore.prototype, "prependAssets", null);
__decorate([
    action.bound
], AssetStore.prototype, "appendAssets", null);
__decorate([
    action.bound
], AssetStore.prototype, "setAssets", null);
__decorate([
    action.bound
], AssetStore.prototype, "setFetchStatus", null);
__decorate([
    action.bound
], AssetStore.prototype, "setPreviousFetchParams", null);
//# sourceMappingURL=AssetStore.js.map